// Reactions used to be a local-only visual toggle. The announcements SDK
// already has a scoped react endpoint; every card and detail picker calls
// `el.call('toggleBroadcastReaction', postId, emoji)` — Broadcast's
// optimistic/persisted mutation — directly at the handler (a module-scope
// `_toggleReaction` delegate used to sit between them: FA204/frank trap,
// bare module consts read as undefined post-toJSON).

// (The reaction picker renders in the shell-root DropdownRoot since
// 2026-06-06 — no per-card popover to keep unclipped anymore. The cover
// gradient block has its own `overflow: hidden` for rounded corners.)
export const AnnouncementCard = {
  tag: 'article',
  flow: 'y',
  round: 'radiusSheet',
  // Fill only — the hairline was redundant against this card's own
  // `overlay` fill. Hover emphasis moves from a border-color swap to a
  // shadow lift (matching tickets/Cards.js's "card outlines → fill + float
  // on hover" conversion in this same sweep).
  background: 'overlay',
  overflow: 'visible',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 220ms ease',
  ':hover': {
    shadow: 'float',
    transform: 'translateY(-3px)'
  },
  onClick: (e, el, s) => {
    // Ignore clicks that originated inside an interactive child (reactions, buttons, links)
    const t = e.target
    if (t && (t.closest('button') || t.closest('a'))) return
    // `selectedAnnouncement` stays as the data carrier the detail body
    // reads from; visibility moved to the @symbo.ls/windows layer
    // (2026-06-06) — ModalRoot owns backdrop, ESC and dismiss.
    s.root.update({ selectedAnnouncement: s })
    el.call('openModal', {
      id: 'announcement-detail',
      template: 'custom',
      width: 'overlayXl',
      maxHeight: '85vh',
      content: { extends: 'AnnouncementDetailModal' }
    })
  },

  AcCover: {
    height: 'F',
    background: (el, s) => s.cover || 'linear-gradient(135deg, #0085FF 0%, #50E1FF 100%)',
    position: 'relative',
    overflow: 'hidden',
    borderTopLeftRadius: 'radiusSheet',
    borderTopRightRadius: 'radiusSheet',

    AcTagPill: {
      position: 'absolute',
      top: 'A',
      left: 'A',
      padding: 'Z A',
      round: 'radiusCard',
      fontSize: 'Y2',
      fontWeight: '700',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      // AMBIGUOUS (left): #0a0a0b has no exact token match (gray1 is #040404,
      // close but not byte-identical) — needs a fixed near-black regardless
      // of theme since it sits on an arbitrary cover image, not page bg.
      color: '#0a0a0b',
      // Deliberately FIXED — pairs with the near-black text above; both
      // must stay fixed so the pill reads over ANY arbitrary cover image.
      background: 'white',
      // A loaded post with no topic carries the default-topic KEY
      // (functions/broadcast.js broadcastFromWire); a real topic passes
      // through polyglot verbatim.
      text: (el, s) => el.call('polyglot', s.tag || 'workspace.broadcastStudio.preview.tagDefault')
    }
  },

  AcBody: {
    flow: 'y',
    gap: 'B',
    padding: 'C',

    AcHeader: {
      flow: 'y',
      gap: 'A',
      AcTitle: {
        tag: 'h2',
        fontSize: 'D',
        fontWeight: '700',
        color: 'title',
        letterSpacing: '-0.025em',
        lineHeight: '1.15',
        text: (el, s) => s.title
      },
      AcExcerpt: {
        tag: 'p',
        color: 'paragraph',
        fontSize: 'A',
        lineHeight: '1.55',
        text: (el, s) => s.excerpt
      }
    },

    AcAuthor: {
      flow: 'x',
      align: 'center',
      gap: 'A',
      // Borderless law — the extra `paddingTop` + the byline's own
      // typography already separate it from AcExcerpt above (same reasoning
      // as shared/AiActionConfirmCard's Header/Footer divider removal).
      paddingTop: 'A',

      AcAvatar: {
        extends: 'Avatar',
        align: 'center center',
        boxSize: 'C',
        round: '50%',
        src: (el, s) => el.call('avatarUrlFor', s.author)
      },
      AcMeta: {
        flow: 'y',
        gap: 'Y',
        flex: '1',
        AcName: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => s.author
        },
        AcSub: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: (el, s) => {
            const parts = []
            if (s.authorRole) parts.push(s.authorRole)
            if (s.date) {
              try {
                parts.push(
                  el.call('localizedDate', s.date, {
                    month: 'short',
                    day: 'numeric'
                  })
                )
              } catch {}
            }
            const readTime = el.call('broadcastReadTime', s)
            if (readTime) parts.push(readTime)
            return parts.join(' · ')
          }
        }
      }
    },

    AcReactions: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      flexWrap: 'wrap',
      AcExisting: {
        flow: 'x',
        align: 'center',
        gap: 'Z',
        flexWrap: 'wrap',
        ':empty': { display: 'none' },
        childExtends: 'ReactionPill',
        childrenAs: 'state',
        children: (el, s) =>
          Object.entries(s.reactions || {}).map(([emoji, names]) => ({
            emoji,
            names,
            postId: s.id
          }))
      },
      AcAddReaction: { extends: 'AddReactionPicker' }
    }
  }
}

export const ReactionPill = {
  tag: 'button',
  flow: 'x',
  align: 'center',
  gap: 'Z',
  padding: 'Y A',
  round: 'radiusPill',
  theme: 'dialog',
  borderWidth: 'hairline',
  borderStyle: 'solid',
  fontSize: 'Z2',
  fontWeight: '700',
  color: 'title',
  cursor: 'pointer',
  title: (el, s) => (s.names || []).join(', '),
  onClick: (ev, el, s) => {
    ev.preventDefault()
    el.call('toggleBroadcastReaction', s.postId, s.emoji)
  },
  // Border highlights when the signed-in user has reacted with this emoji.
  borderColor: (el, s) => {
    const myName = el.call('findMe', s)?.name
    return myName && (s.names || []).includes(myName) ? 'blue' : 'transparent'
  },
  RpEmoji: { tag: 'span', fontSize: 'A', text: (el, s) => s.emoji },
  RpCount: { tag: 'span', text: (el, s) => String((s.names || []).length) }
}

// FA204 / frank trap — these tables are captured BY VALUE into
// ArpPopoverMenu's `scope` below (module-eval-time snapshot, serialized as
// data by frank.toJSON) and the grid factories read `el.scope.*`. A factory
// body reading the bare module const at CALL time would be undefined after
// a platform push and both grids would mount empty.
const POPULAR_EMOJIS = ['👍', '❤️', '🎉', '🚀', '🔥', '👏', '👀', '😂']
const ALL_EMOJIS = [
  '👍',
  '👎',
  '❤️',
  '🧡',
  '💛',
  '💚',
  '💙',
  '💜',
  '🎉',
  '🎊',
  '🚀',
  '🔥',
  '⚡',
  '✨',
  '💯',
  '⭐',
  '👏',
  '🙌',
  '🤝',
  '💪',
  '🙏',
  '👀',
  '😂',
  '😅',
  '😍',
  '🤩',
  '😎',
  '🤯',
  '🥳',
  '🤔',
  '😢',
  '😡',
  '✅',
  '❌',
  '⚠️',
  '💡',
  '📈',
  '📉',
  '📺',
  '🍕'
]

// Add-reaction picker — migrated 2026-06-06 to the @symbo.ls/dropdown
// plugin. The previous bespoke ArpPopover (absolutely-positioned panel
// per card, imperative querySelectorAll open/close + per-card article
// z-index bumping to escape sibling stacking contexts) is gone: the
// plugin's shell-mounted DropdownRoot renders ONE panel above
// everything, owns positioning (auto-flip), outside-click and ESC.
// `_arpPostId` on root state carries which post the picker targets —
// same data-carrier pattern as selectedAnnouncement above.
export const AddReactionPicker = {
  position: 'relative',

  ArpButton: {
    tag: 'button',
    align: 'center center',
    padding: 'Y A',
    round: 'radiusPill',
    theme: 'dialog',
    border: 'none',
    fontSize: 'A',
    fontWeight: '700',
    color: 'caption',
    cursor: 'pointer',
    text: '+',
    'aria-label': (el) => el.call('polyglot', 'workspace.announcements.addReaction'),
    ':hover': { color: 'title' },
    onClick: (ev, el, s) => {
      ev.preventDefault()
      ev.stopPropagation()
      s.root.update({ _arpPostId: s.id })
      el.call('openDropdown', {
        anchorEl: el,
        id: 'arpPicker',
        placement: 'bottom-end',
        minWidth: '360px',
        // Shell paint opt-in — ArpPopoverMenu is body-only ("the panel
        // supplies theme/round/shadow"); the plugin Panel is chromeless
        // by default (FW-DROPDOWN-PANEL-STALE-SHELL-PAINT-1).
        theme: 'canvas-card-glass',
        shadow: 'overlay',
        content: { extends: 'ArpPopoverMenu' }
      })
    }
  }
}

// Body-only emoji picker rendered inside the dropdown plugin's panel.
// Reads the target post from `root._arpPostId` (written by ArpButton
// the tick before opening). The panel supplies theme/round/shadow/
// maxHeight+scroll.
export const ArpPopoverMenu = {
  scope: { POPULAR_EMOJIS, ALL_EMOJIS },
  tag: 'div',
  flow: 'y',
  gap: 'A',
  padding: 'Z',
  role: 'menu',

  ArpPopular: {
    flow: 'y',
    gap: 'Z',
    ArpPopHead: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      text: '{{ workspace.reaction.popular | polyglot }}'
    },
    ArpPopGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(8, 1fr)',
      gap: 'Z',
      childExtends: 'AddReactionEmoji',
      childrenAs: 'state',
      children: (el, s) =>
        el.scope.POPULAR_EMOJIS.map((e) => ({ emoji: e, postId: s.root?._arpPostId }))
    }
  },

  ArpAll: {
    flow: 'y',
    gap: 'Z',
    // Borderless law — the "Popular"/"All" uppercase caption labels + the
    // extra `paddingTop` already separate the two emoji groups.
    paddingTop: 'A',
    ArpAllHead: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      text: '{{ workspace.reaction.all | polyglot }}'
    },
    ArpAllGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(8, 1fr)',
      gap: 'Z',
      childExtends: 'AddReactionEmoji',
      childrenAs: 'state',
      children: (el, s) =>
        el.scope.ALL_EMOJIS.map((e) => ({ emoji: e, postId: s.root?._arpPostId }))
    }
  }
}

export const AddReactionEmoji = {
  tag: 'button',
  align: 'center center',
  width: 'C',
  height: 'C',
  round: 'radiusCard',
  background: 'transparent',
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'transparent',
  fontSize: 'B',
  cursor: 'pointer',
  text: (el, s) => s.emoji,
  ':hover': { background: 'hover', borderColor: 'line' },
  onClick: (ev, el, s) => {
    ev.preventDefault()
    ev.stopPropagation()
    el.call('toggleBroadcastReaction', s.postId, s.emoji)
    el.call('closeDropdown', 'arpPicker')
  }
}

// Body-only announcement detail — rendered inside the @symbo.ls/windows
// ModalRoot via `openModal({ template: 'custom', content: { extends:
// 'AnnouncementDetailModal' } })` (AnnouncementCard.onClick above).
// Backdrop, ESC and dismiss-on-backdrop are owned by the windows
// ModalLayer; the previous bespoke `extends: 'Modal'` + stateKey +
// ModalWindow wrapper is gone (migrated 2026-06-06). Reads its data
// from `root.selectedAnnouncement`, which the card click still writes.
export const AnnouncementDetailModal = {
  // Window chrome owns the outer sizing — the openModal call passes
  // width overlayXl / maxHeight 85vh on the layer descriptor. The body
  // just fills it and scrolls.
  width: '100%',
  maxHeight: '85vh',
  overflow: 'auto',
  flow: 'y',
  position: 'relative',

  // Close X — the brand ModalWindow used to supply this; body-only
  // content brings its own. Floats over the cover image top-right.
  // THE house close X (brand CloseButton — F2, one X for every panel);
  // geometry + glyph inherited, only the scrim ink below is local.
  AdmClose: {
    extends: 'CloseButton',
    position: 'absolute',
    top: 'Y2',
    right: 'Y2',
    zIndex: '2',
    // Deliberately FIXED: floats over AdmCover's arbitrary announcement
    // cover image/gradient below — same scrim-over-arbitrary-imagery
    // contract as ModalLayer's own fixed 'black.45' backdrop.
    color: 'white',
    background: 'black.45',
    ':hover': { background: 'black.7', color: 'white' },
    'aria-label': (el) => el.call('polyglot', 'workspace.announcements.closeLabel'),
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('closeModal', 'announcement-detail')
    }
  },

  AdmCover: {
    hide: (el, s) => !s.root?.selectedAnnouncement?.cover,
    height: 'G',
    flexShrink: '0',
    background: (el, s) =>
      s.root?.selectedAnnouncement?.cover || 'linear-gradient(135deg, #0085FF 0%, #50E1FF 100%)',
    borderTopLeftRadius: 'radiusSheet',
    borderTopRightRadius: 'radiusSheet'
  },

  AdmBody: {
    flow: 'y',
    gap: 'B',
    padding: 'D',

    AdmTagPill: {
      tag: 'span',
      alignSelf: 'flex-start',
      padding: 'Y A',
      round: 'radiusPill',
      fontSize: 'Y2',
      fontWeight: '700',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'blue',
      background: 'blue.2',
      text: (el, s) => s.root?.selectedAnnouncement?.tag || ''
    },

    AdmTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'E',
      fontWeight: '700',
      letterSpacing: '-0.025em',
      lineHeight: '1.1',
      color: 'title',
      text: (el, s) => s.root?.selectedAnnouncement?.title || ''
    },

    AdmAuthor: {
      flow: 'x',
      align: 'center',
      gap: 'A',
      // Borderless law — the extra `paddingBottom` + byline typography
      // already separate this from AdmExcerpt below.
      paddingBottom: 'B',

      AdmAvatar: {
        extends: 'Avatar',
        align: 'center center',
        boxSize: 'C',
        round: '50%',
        src: (el, s) => el.call('avatarUrlFor', s.root?.selectedAnnouncement?.author)
      },
      AdmMeta: {
        flow: 'y',
        gap: 'Y',
        flex: '1',
        AdmAuthorName: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => s.root?.selectedAnnouncement?.author || ''
        },
        AdmAuthorSub: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: (el, s) => {
            const a = s.root?.selectedAnnouncement
            if (!a) return ''
            const parts = []
            if (a.authorRole) parts.push(a.authorRole)
            if (a.date) {
              try {
                parts.push(
                  el.call('localizedDate', a.date, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })
                )
              } catch {}
            }
            const readTime = el.call('broadcastReadTime', a)
            if (readTime) parts.push(readTime)
            return parts.join(' · ')
          }
        }
      }
    },

    AdmExcerpt: {
      tag: 'p',
      margin: '0',
      fontSize: 'B',
      lineHeight: '1.55',
      color: 'paragraph',
      fontWeight: '500',
      text: (el, s) => s.root?.selectedAnnouncement?.excerpt || ''
    },

    AdmExternalLink: {
      extends: 'Link',
      hide: (el, s) => {
        const post = s.root?.selectedAnnouncement
        return post?.audience !== 'external' || !post?.externalUrl
      },
      href: (el, s) => s.root?.selectedAnnouncement?.externalUrl || '#',
      target: '_blank',
      rel: 'noreferrer',
      alignSelf: 'flex-start',
      padding: 'Z A',
      round: 'radiusPill',
      background: 'overlay',
      color: 'title',
      fontSize: 'Z1',
      fontWeight: '700',
      textDecoration: 'none',
      text: '{{ workspace.rows.openExternal | polyglot }}'
    },

    AdmContent: {
      flow: 'y',
      gap: 'A',
      childExtends: 'AdmParagraph',
      childrenAs: 'state',
      children: (el, s) => {
        const body = s.root?.selectedAnnouncement?.body
        if (!body) return []
        if (Array.isArray(body)) return body.map((p) => ({ text: p }))
        if (typeof body === 'string') return [{ text: body }]
        return []
      }
    }
  }
}

export const AdmParagraph = {
  tag: 'p',
  margin: '0',
  fontSize: 'A',
  lineHeight: '1.65',
  color: 'paragraph',
  text: (el, s) => s.text || ''
}

export const IntroRun = {
  tag: 'span',
  fontSize: 'A',
  color: (el, s) => s.color || 'caption',
  fontWeight: (el, s) => s.weight || '500',
  text: (el, s) => s.text
}

export const AvatarBundle = {
  display: 'flex',
  flexFlow: 'row',
  align: 'center',
  childExtends: 'AvatarBundleItem',
  childrenAs: 'state',
  // The overlap stack pulls each item's right edge under its neighbor via a
  // negative margin — the LAST item must not bleed that margin into the next
  // flex sibling (it was shoving the "N people" caption under the stack).
  childProps: { ':last-child': { marginInlineEnd: '0' } },
  children: (el, s) => {
    let emails = el.emails || []
    if (typeof emails === 'function') emails = emails(el, s) || []
    const lookup = el.call('peopleByEmail')
    const out = []
    for (const e of emails) {
      const p = lookup[e]
      if (p) out.push(p)
      if (out.length === 3) break
    }
    return out
  }
}

export const AvatarBundleItem = {
  // ONE URL predicate for the plate's readers (AvatarImg if/src + Initials
  // hide) so the three cannot drift — in `scope`, not a module const
  // (FA204: a bare module const reads as undefined post-frank.toJSON).
  scope: {
    avatarUrlOf: (s) => {
      const v = s && s.avatar
      return typeof v === 'string' && /^(https?:|\/)/.test(v) ? v : null
    }
  },
  position: 'relative',
  zIndex: '1',
  marginInlineEnd: '-Z1',
  cursor: 'default',
  // KEPT — avatar-stack ring, the sanctioned separator so overlapping
  // avatars read as distinct circles. Retokenized `'2px'` → `hairline2`.
  borderWidth: 'hairline2',
  borderStyle: 'solid',
  borderColor: 'overlay',
  round: '50%',
  // Native title as identification (consumers that layer a custom tooltip —
  // TzAvatarItem — override with `title: ''`). Declarative, replacing the
  // former onRender setAttribute + addEventListener block (v3-era raw DOM;
  // the hover z-index bump is pure CSS).
  title: (el, s) => (s && s.name) || (s && s.email) || '',
  ':hover': { zIndex: '50' },

  // House identity plate (PersonCard.PcAvatar precedent, 2026-08-08 §4.3):
  // neutral `overlayStrong` wash + `title` ink initials, real photo only when
  // `s.avatar` is a URL. Replaces the dicebear per-seed rainbow plates —
  // 5 of those 7 hues failed AA under white ink, and a multicolor stack
  // fights the one-hue-per-screen law on every poster widget.
  BundlePlate: {
    align: 'center center',
    width: 'B1',
    height: 'B1',
    round: '50%',
    overflow: 'hidden',
    position: 'relative',
    background: 'overlayStrong',
    color: 'title',
    fontSize: 'Z2',
    fontWeight: '700',

    AvatarImg: {
      tag: 'img',
      // `if:`, never `hide:` — a display:none <img> still FETCHES its src
      // ('N'/'HL'-style letters 404ed against the origin on every load,
      // measured live 2026-09-01). No node, no request.
      if: (el, s) => !!el.scope.avatarUrlOf(s),
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      // Belt on top of the if: gate — null emits no src attribute at all
      // (src:'' is itself a request of the page URL in some engines).
      src: (el, s) => el.scope.avatarUrlOf(s),
      alt: (el, s) => (s && (s.name || s.email)) || 'member'
    },

    Initials: {
      tag: 'span',
      hide: (el, s) => !!el.scope.avatarUrlOf(s),
      // Georgian stays as typed: String#toUpperCase maps Mkhedruli to
      // MTAVRULI capitals (Unicode 11) — the home face stack showed ᲒᲑ over
      // rows reading გბ for the same people (crm-w1 twin QA, F6). Every
      // cased script is still upper-cased (crmInitials, the eyebrow rule).
      text: (el, s) => {
        const label = (s && (s.name || s.email)) || '?'
        const parts = String(label).trim().split(/\s+/).filter(Boolean)
        const pair = parts.length >= 2 ? parts[0][0] + parts[1][0] : String(label).slice(0, 2)
        return /\p{Script=Georgian}/u.test(pair) ? pair : pair.toUpperCase()
      }
    }
  }
}

// FA204 / frank trap — emailByName lives in `scope`, NOT module scope
// (a bare module const reads as undefined post-frank.toJSON and the click
// handler would silently stop opening profiles).
export const BirthdayItem = {
  scope: {
    emailByName: (s, name) => {
      if (!name) return null
      const list = (s && s.root && s.root.people) || []
      const lc = String(name).toLowerCase()
      for (const p of list) {
        if ((p.name || '').toLowerCase() === lc) return p.email
      }
      return null
    }
  },
  flow: 'x',
  align: 'center',
  gap: 'A',
  cursor: 'pointer',
  transition: 'transform 160ms ease',
  ':hover': { transform: 'translateY(-2px)' },
  onClick: (e, el, s) => {
    e.preventDefault()
    const email = el.scope.emailByName(s, s.name)
    if (email) el.call('openProfileModal', email)
  },

  BdAvatar: {
    align: 'center center',
    width: 'C',
    height: 'C',
    round: '50%',
    // Opaque identity tile whose hue comes from DATA (s.color). One fixed ink
    // cannot serve every hue (white 3.62 on blue, near-black 3.0 on purple), so
    // `inkOnPlate` measures the plate and picks — same as AgentHeroCard's Glyph.
    color: (el, s) => el.call('inkOnPlate', s.color || 'blue'),
    fontSize: 'Z2',
    fontWeight: '700',
    background: (el, s) => s.color || 'blue',
    text: (el, s) => s.initials || '',
    // Flat v3.14 title prop — was a v3-era onRender setAttribute (same
    // migration as AvatarBundleItem above).
    title: (el, s) => s.name || ''
  },
  BdName: {
    tag: 'span',
    fontSize: 'A',
    color: 'title',
    fontWeight: '700',
    text: (el, s) => s.name
  }
}

export const UpcomingEventCard = {
  // Plain button (was `extends: 'Link'` with href `/calendar#event=…`,
  // which redirected the user to the full calendar page). Now opens
  // the CalEventDetailModal in place via @symbo.ls/windows so the
  // user stays on the dashboard — same dispatch the calendar's own
  // WeekView / MonthView event cells use (`openEventDetailModal`).
  tag: 'button',
  type: 'button',
  textAlign: 'left',
  color: 'inherit',
  cursor: 'pointer',
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'Z A',
  round: 'radiusSheet',
  background: 'overlay',
  border: 'none',
  transition: 'all 200ms ease',
  ':hover': { theme: 'dialog', transform: 'translateY(-2px)' },

  onClick: (e, el, s) => {
    e.preventDefault()
    // Open the event detail modal via @symbo.ls/windows (CalEventDetailModal
    // is a windows-body component, not a Modal). Same dispatch the
    // calendar's WeekView / MonthView event cells use.
    el.call('openEventDetailModal', s)
  },

  // Ink-wash date tile — was `background: 'blue.2'` + `color: 'blue'`, a
  // SECOND accent hue (lavender/purple) landing on the "Coming up" widget's
  // single red-orange poster (homeWidgetHue's 'events' entry), violating
  // design-direction.md §6 "one accent hue per surface" (2026-08-12 per
  // Fable's live walk). The card's own ink is fixed 'white' (poster.ink for
  // 'events' — this component only ever mounts inside that ONE poster, same
  // fixed-ink convention as CityMemberRow above), so the tile is now a plain
  // white ink-wash fill instead of a borrowed hue.
  UeDate: {
    flow: 'y',
    align: 'center center',
    width: 'C',
    minWidth: 'C',
    padding: 'Z',
    round: 'radiusSheet',
    background: 'white.12',
    UeDay: {
      tag: 'span',
      fontSize: 'B',
      fontWeight: '700',
      color: 'white',
      lineHeight: '1',
      text: (el, s) => {
        try {
          return String(new Date(s.startAt).getDate())
        } catch {
          return ''
        }
      }
    },
    UeMonth: {
      // MEASURED secondary step, not an opacity floor — white `.75` on the
      // 'events' poster read 3.58:1, under the 4.5:1 AA floor for normal
      // text (HOME-POSTER-EMPTYSTATE-INK-1, live 2026-08-22).
      extends: 'HomePosterSoftInk',
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      textTransform: 'uppercase',
      text: (el, s) => {
        try {
          return el.call('localizedDate', s.startAt, { month: 'short' })
        } catch {
          return ''
        }
      }
    }
  },

  UeBody: {
    flow: 'y',
    gap: 'Y',
    flex: '1',
    minWidth: '0',
    UeTitle: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      // Fixed 'white' ink, not adaptive 'title' — this card only ever mounts
      // inside the EventsWidget's ONE red-orange poster (homeWidgetHue's
      // 'events' entry: ink 'white'). Adaptive 'title' flips to near-black in
      // light theme (color.js title:['gray1','gray15']), which would read
      // fine in dark theme by coincidence but put near-black text on the red
      // poster in light theme — same theme-invariant-poster bug UeDate/
      // UeMonth below and CityMemberRow were fixed for (2026-08-12).
      color: 'white',
      // Explicit wrap rules — the card sits in a narrow grid column so a
      // long title (e.g. "Launching plans for Q3") must wrap at spaces,
      // never get cut mid-word. Prevents the "launching pla" truncation
      // the home brief flagged.
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      text: (el, s) => s.title
    },
    UeMeta: {
      // The poster's MEASURED secondary step, resolved off the card's own
      // poster (HomePosterSoftInk). The old form was fixed 'white' at the
      // blanket `.55` opacity floor — the geex ink language's grammar
      // default, never measured: it read 2.58:1 on the 'events' poster,
      // under the 4.5:1 AA floor for normal text
      // (HOME-POSTER-EMPTYSTATE-INK-1, live 2026-08-22). The resolver keeps
      // the fixed-ink poster rationale of UeTitle above (it returns the
      // poster's OWN binary ink, never the adaptive 'caption', inside a
      // poster) and follows a branded workspace's override at paint time.
      extends: 'HomePosterSoftInk',
      tag: 'span',
      fontSize: 'Z2',
      // Single line with ellipsis — the source data sometimes stuffs a
      // long description into `location`, so without a clamp the meta
      // wraps and gets visually cut mid-word ("…launching pla") by the
      // grid row. One-line ellipsis keeps the card height predictable
      // and the truncation honest (a real "…", not a mid-word slice).
      display: 'block',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        let t = ''
        try {
          t = el.call('localizedDate', s.startAt, {
            hour: '2-digit',
            minute: '2-digit'
          })
        } catch {}
        return `${t}${t && s.location ? ' · ' : ''}${s.location || ''}`
      }
    },

    // JoinBtn — board §2 "next events with JOIN button for meet-linked
    // ones". `s` here IS the raw event object (childrenAs:'state' in the
    // widget's own `children` builder — same contract onClick above
    // already relies on for openEventDetailModal), so el.call passes it
    // straight through to functions/eventMeetJoin.js's eventHasMeetLink /
    // joinEventCall, which reuse the calendar package's OWN join dispatch
    // (EventDetailModal's "Join call" SaveBtn) verbatim.
    //
    // `tag: 'span'` + role/tabindex, NOT a real `<button>` — this card's
    // OWN root is already `tag: 'button'`, and a `<button>` nested inside
    // a `<button>` is invalid HTML5 (the parser auto-closes the outer tag
    // and silently pulls every sibling after this one out of the card —
    // same documented trap BroadcastNewsRow's Star button works around).
    // stopPropagation so Join doesn't ALSO trigger the card's own onClick
    // (open the full detail modal) on the same click.
    JoinBtn: {
      hide: (el, s) => !el.call('eventHasMeetLink', s),
      tag: 'span',
      role: 'button',
      tabindex: '0',
      flow: 'x',
      align: 'center',
      gap: 'V',
      alignSelf: 'flex-start',
      marginTop: 'V',
      padding: 'V Z',
      round: 'radiusPill',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      // Inverted-pill CTA, same fixed-poster-ink convention as UeDate/
      // UeTitle/UeMeta above (white 'title'/'caption' would flip with the
      // shell theme on this theme-invariant red-orange poster) — solid
      // white fill + the poster's own red as the text, mirroring
      // _taskQuickAddRow's AddBtn (hue.ink bg / hue.bg text) one level up
      // in fixed-value form since this card has no hue object passed in.
      background: 'white',
      color: 'red',
      transition: '120ms defaultBezier opacity',
      ':hover': { opacity: '.85' },
      'aria-label': (el) => el.call('polyglot', 'workspace.rows.joinCall'),
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('joinEventCall', s)
      },
      onKeydown: (e, el, s) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return
        e.preventDefault()
        e.stopPropagation()
        el.call('joinEventCall', s)
      },
      Icon: { name: 'video', boxSize: 'Y1', color: 'red' },
      JoinLabel: { tag: 'span', text: '{{ workspace.rows.join | polyglot }}' }
    }
  }
}

// Team-timezones MEMBER row — one row per teammate (avatar, name, city,
// live local time), a purely INFORMATIONAL row: no navigation (it used to
// be a Link to /team-members, which was misleading — clicking a timezones
// card dropped the user on the team-members page with no timezone context,
// ticket 6a3463e9 — there is still no dedicated timezones page to point at).
//
// Replaces the old per-CITY cluster tile (2026-08-12 per Fable's live walk):
// a small team collapsed to ONE cluster tile in a `repeat(auto-fit)` grid,
// leaving ~70% dead space below it. A flat member list always fills the
// body proportionally to headcount instead of city-bucket count.
//
// Ink is hardcoded 'black' rather than the adaptive 'title'/'caption'
// tokens the old CityCluster used. THIS WAS THE ACTUAL BUG (not just the
// dead space): 'title'/'caption' flip with the shell's light/dark theme,
// but the widget's poster surface (homeWidgetHue's 'teamtime' entry —
// cerulean+30 bg, BLACK ink) is theme-invariant by design, so in dark
// theme the adaptive tokens rendered near-WHITE text on the light cyan
// poster — near-invisible. This component only ever mounts inside that
// ONE poster, so a fixed ink is deliberate here (same convention as
// BdAvatar's fixed 'white' identity ink above), not a raw-colour violation.
//
// Working-hours bar (board §2 "teamtime: ... working-hours bar") — derived
// purely from `s.timezone`, the SAME field CmTime already reads below, no
// new fetch/state.
//
// FA204 / frank trap — hourFraction/isWorkingHours live in `scope`, NOT
// module scope: a bare module-scope const silently reads as undefined once
// the component is frank.toJSON'd (factories then throw and the subtree
// never mounts). `el.scope.<name>` resolves from any descendant factory or
// handler via the prototype chain (GeexBlobPercent/d6ea1142 precedent).
export const CityMemberRow = {
  scope: {
    hourFraction: (timezone) => {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23'
        }).formatToParts(new Date())
        const h = Number(parts.find((p) => p.type === 'hour')?.value)
        const m = Number(parts.find((p) => p.type === 'minute')?.value)
        if (Number.isNaN(h)) return 0
        return (h + (Number.isNaN(m) ? 0 : m / 60)) / 24
      } catch {
        return 0
      }
    },
    isWorkingHours: (timezone) => {
      let hour = 0
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23'
        }).formatToParts(new Date())
        const h = Number(parts.find((p) => p.type === 'hour')?.value)
        const m = Number(parts.find((p) => p.type === 'minute')?.value)
        if (!Number.isNaN(h)) hour = h + (Number.isNaN(m) ? 0 : m / 60)
      } catch {
        hour = 0
      }
      return hour >= 9 && hour < 18
    }
  },
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'Z A',
  round: 'radiusControl',
  // Ink-wash row separation, no hairline borders (house convention: rounded
  // list rows separate via a gap + tint, never a border) — deepens on hover.
  background: 'black.06',
  transition: '120ms defaultBezier background',
  ':hover': { background: 'black.12' },

  // Key-based auto-extend (bare 'Avatar' key resolves to the built-in — same
  // convention AvatarBundleItem.Avatar above uses), not an aliased extends.
  Avatar: {
    avatarType: 'initials',
    boxSize: 'B B',
    flexShrink: '0',
    src: (el, s) => el.call('avatarUrl', s.name || s.email)
  },

  CmBody: {
    flow: 'y',
    gap: '0',
    flex: '1',
    minWidth: '0',

    CmName: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'black',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name || s.email || ''
    },
    CmCity: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'black',
      opacity: '0.55',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.location || el.call('polyglot', 'workspace.teamTime.remote')
    }
  },

  // Live local time, Code-family numerals (matches the giant-numeral
  // register the other posters on this board use for their headline digits).
  CmTime: {
    tag: 'span',
    fontSize: 'B',
    fontWeight: '700',
    fontFamily: 'Code',
    color: 'black',
    flexShrink: '0',
    text: (el, s) => {
      try {
        return new Date()
          .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: s.timezone,
            hour12: true
          })
          .toLowerCase()
          .replace(' ', '')
      } catch {
        return ''
      }
    }
  },

  // Working-hours bar — a plain 24h track with the conventional 9am-6pm
  // band lit and a marker at the teammate's current local hour (solid =
  // in hours, faded = outside). left/width percentages are DATA-derived
  // fractions of a day (same convention as the analytics/agents widgets'
  // SegBar `flex` fractions elsewhere in this project), not stand-ins for
  // a spacing token.
  CmHours: {
    flexShrink: '0',
    width: 'E',

    CmHoursTrack: {
      position: 'relative',
      height: 'V',
      round: 'radiusPill',
      overflow: 'hidden',
      background: 'black.12',

      CmHoursBand: {
        position: 'absolute',
        top: '0',
        bottom: '0',
        left: '37.5%',
        width: '37.5%',
        background: 'black.15'
      },
      CmHoursMark: {
        position: 'absolute',
        top: '0',
        bottom: '0',
        width: 'V',
        round: 'radiusPill',
        background: (el, s) => (el.scope.isWorkingHours(s.timezone) ? 'black' : 'black.35'),
        left: (el, s) => `${Math.min(96, Math.max(0, el.scope.hourFraction(s.timezone) * 100))}%`
      }
    },

    CmHoursLabel: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'black',
      opacity: '0.45',
      whiteSpace: 'nowrap',
      text: (el, s) =>
        el.call(
          'polyglot',
          el.scope.isWorkingHours(s.timezone)
            ? 'workspace.teamTime.workingHours'
            : 'workspace.teamTime.offHours'
        )
    }
  }
}

// Team-timezones CLUSTER row (2026-08-13, replaces CityMemberRow above as
// the Team-timezones widget's per-item template — CityMemberRow itself is
// kept in place since comments elsewhere still point at it as the
// fixed-ink-poster precedent). One row per distinct IANA timezone: zone
// label + giant live local time on top, an AvatarBundle of that zone's
// members + a headcount caption below. `pages/main.js` groups `root.people`
// by `.timezone` and feeds each group here as `s` (`{ timezone, members }`).
// FA204 / frank trap — zoneTime lives in `scope`, NOT module scope: a bare
// module-scope const silently reads as undefined once the component is
// frank.toJSON'd (works in local parcel, dies after platform push —
// factories throw and the subtree never mounts). `el.scope.<name>` resolves
// from any descendant factory or handler via the prototype chain
// (GeexBlobPercent/d6ea1142 precedent).
//
// zoneLabel itself (TZ-LABELS-1) is no longer a local copy here — it's the
// canonical `el.call('zoneLabel', tz)` in `shared/functions/zoneLabel.js`,
// so `analytics/pages/main.js`'s TeamGrid (which used to render the raw
// IANA identifier straight to the DOM) and this widget share ONE
// implementation instead of two copies drifting apart again later.
export const TzCluster = {
  scope: {
    // Live local time — same format CityMemberRow's CmTime above used: one
    // Code-mono reading, lowercase, no space ("4:32pm"). '—' when the zone
    // can't be resolved — reserves the slot instead of collapsing it
    // (zero-shift status UI, product-UI brief §8).
    zoneTime: (timezone) => {
      if (!timezone) return '—'
      try {
        return new Date()
          .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: timezone,
            hour12: true
          })
          .toLowerCase()
          .replace(' ', '')
      } catch {
        return '—'
      }
    }
  },
  flow: 'y',
  gap: 'Y',
  padding: 'Z A',
  round: 'radiusControl',
  // Ink-wash row separation, no hairline borders — the same house
  // convention CityMemberRow used above (gap + tint, deepens on hover;
  // design-direction.md §8 borderless law).
  background: 'black.06',
  transition: '120ms defaultBezier background',
  ':hover': { background: 'black.12' },

  TzHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'A',

    TzZone: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'black',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => el.call('zoneLabel', s.timezone)
    },

    // Giant focal numeral — Code mono, heavy, fixed black ink (this
    // poster is theme-invariant cerulean+30/black — homeWidgetHue's
    // HOUSE_HUE_MAP — same reasoning CmTime/CmHours above documented for
    // hardcoding ink on this exact card).
    TzTime: {
      tag: 'span',
      fontSize: 'D',
      fontWeight: '700',
      fontFamily: 'Code',
      lineHeight: '1',
      color: 'black',
      flexShrink: '0',
      // Zero-shift: the '—' placeholder keeps the numeral slot reserved for
      // the no-timezone bucket, but at full ink a giant em-dash reads as
      // window chrome (a minimize glyph) — drop it to the §6 inactive
      // alpha; live readings render at full ink.
      opacity: (el, s) => (s.timezone ? '1' : '0.35'),
      text: (el, s) => el.scope.zoneTime(s.timezone)
    }
  },

  TzFoot: {
    flow: 'x',
    align: 'center',
    gap: 'Z',

    // House usage — bare AvatarBundle (key-based auto-extend, Rule 63) +
    // `emails:` (StoryCard.ScStack's exact pattern), capped at 3 with the
    // remainder covered by TzCount below (same as ScStack + ScCaption).
    // `childExtends` overridden to a registered item that layers click +
    // tooltip + presence on top of the shared AvatarBundleItem chrome
    // (avatar image, overlap, hover z-index bump all inherited unchanged).
    AvatarBundle: {
      flexShrink: '0',
      emails: (el, s) => (s.members || []).map((m) => m.email),
      childExtends: 'TzAvatarItem'
    },

    TzCount: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'black',
      opacity: '0.55',
      text: (el, s) => {
        const n = (s.members || []).length
        return `${n} ${n === 1 ? 'person' : 'people'}`
      }
    }
  }
}

export const TzAvatarItem = {
  extends: 'AvatarBundleItem',
  // Self-contained copy of TzCluster.scope.zoneTime (NOT a module-scope
  // helper — FA204/frank trap; and not a reach into the ancestor's scope,
  // so this item stays mountable outside a TzCluster). Same self-contained
  // posture as shared/functions/peopleUtils.js documents in its header.
  scope: {
    zoneTime: (timezone) => {
      if (!timezone) return '—'
      try {
        return new Date()
          .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: timezone,
            hour12: true
          })
          .toLowerCase()
          .replace(' ', '')
      } catch {
        return '—'
      }
    }
  },
  cursor: 'pointer',
  // Custom tooltip (openTooltip below) owns the hover surface — blank the
  // native title AvatarBundleItem declares so the browser tooltip doesn't
  // double up over it.
  title: '',
  // Poster-invariant plate: this widget's surface is the fixed
  // cerulean+30 poster (theme-invariant), so the adaptive
  // overlayStrong/title pair would flip to light-on-light in dark mode —
  // pin the plate to the poster's OWN derived ink system (homeWidgetHue's
  // {fill, ink} contract — same source TzOnlineDot's ring already reads,
  // same reasoning as TzZone/TzTime's fixed ink above).
  // 2026-09-02: resolved through `homeWidgetSurfaceHue` (the widget's colour
  // is now the USER's choice, neutral by default), and the fallbacks moved
  // from the fixed `black.15` / `black` pair to the shell's ADAPTIVE tokens —
  // a fixed black ink was right only while this card was guaranteed to carry
  // the pale cerulean poster, and reads black-on-dark the moment it does not.
  BundlePlate: {
    background: (el, s) =>
      (el.call('homeWidgetSurfaceHue', s.root, 'teamtime') || {}).fill || 'overlay',
    color: (el, s) => (el.call('homeWidgetSurfaceHue', s.root, 'teamtime') || {}).ink || 'title'
  },
  // Adaptive 'overlay' would collide with this fixed-ink poster the same
  // way the home-board "Team & agents" widget's Avatars row documents for
  // its own borderColor override (pages/main.js) — ring matches the
  // poster's own bg so it reads as a punched-out seam instead.
  borderColor: (el, s) =>
    (el.call('homeWidgetSurfaceHue', s.root, 'teamtime') || {}).bg || 'dialog',
  role: 'button',
  tabindex: '0',
  aria: {
    label: (el, s) =>
      el
        .call('polyglot', 'workspace.teamTime.openAria')
        .replace('{name}', s.name || s.email || el.call('polyglot', 'workspace.teamTime.profile'))
  },
  // Focus ring comes free from app.js's global
  // `:where([role="button"],[tabindex]):focus-visible` floor — no
  // per-component outline needed here.

  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('closeTooltip')
    if (s.email) el.call('openProfileModal', s.email)
  },
  onKeydown: (e, el, s) => {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return
    e.preventDefault()
    el.node.click()
  },
  onMouseenter: (e, el, s) => {
    if (!s.email) return
    const zone = el.scope.zoneTime(s.timezone)
    const bits = [s.name || s.email, s.position, zone !== '—' ? zone : null].filter(Boolean)
    el.call('openTooltip', { anchorEl: el, text: bits.join(' · '), placement: 'top' })
  },
  onMouseleave: (e, el) => el.call('closeTooltip'),

  TzOnlineDot: {
    // Same per-id presence map PersonCard's PcOnlineDot reads. `hide:`
    // (not `if:`) so a LATER presence sync can still light the dot up
    // without a remount — `if:` is a mount-time-only gate (see
    // OnlineMembers.js's file-header note: the documented "dock stayed
    // empty forever" bug this avoids).
    hide: (el, s) => !(s.root?.presenceById || {})[String(s.email || '').toLowerCase()],
    position: 'absolute',
    bottom: '0',
    right: '0',
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: 'green',
    // KEPT — punched-out-seam presence ring (see this file's own comment
    // above). Retokenized `'2px'` → `hairline2`.
    borderWidth: 'hairline2',
    borderStyle: 'solid',
    borderColor: (el, s) =>
      (el.call('homeWidgetSurfaceHue', s.root, 'teamtime') || {}).bg || 'dialog'
  }
}

// ── HomeMailRow — one thread row on the home `mail-inbox` widget ────────────
// architecture/MAIL.md §3.6. A COMPACT read of mail's own MailThreadRow:
// sender, subject, a Gmail-scale timestamp, and weight as the unread signal.
//
// DISPLAY ONLY, deliberately — no `tag: 'button'` and no onClick. The card it
// sits in carries `openRoute: '/mail'`, so a click anywhere on the row already
// opens the app; making the row its own control would put a second, narrower
// target over the same destination and add an interactive descendant the card
// has to route around. The one control on this card is Compose, which goes
// somewhere else.
//
// Fields are read FLAT off the row state (`s.subject`, `s.participants`) — the
// shape mail's `normalizeMailThread` guarantees. `unreadCount` is per THREAD,
// not per account: a thread with any unread message reads bold, the same
// signal mail's own list uses.
//
// The sender is derived inline rather than through mail's
// `mailParticipantsLabel`: this file is in the SHELL's chunk and must not pull
// the mail package's bundle into the home page for one label. The derivation
// is the same one mail's ThreadList makes — the `from` participant, first name
// preferred, the local part of the address as the fallback.
export const HomeMailRow = {
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'Y 0',
  minWidth: '0',

  HmrBody: {
    flow: 'y',
    gap: '0',
    flex: '1',
    minWidth: '0',

    HmrFrom: {
      tag: 'span',
      fontSize: 'Z1',
      // The unread signal is WEIGHT, not a second colour: the card may carry
      // any user-picked hue, and a fixed accent would read as a second one.
      fontWeight: (el, s) => (Number(s?.unreadCount || 0) > 0 ? '700' : '500'),
      color: 'inherit',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        const list = (s && s.participants) || []
        const from = list.find((p) => p && p.role === 'from') || list[0] || {}
        const email = String(from.email || '')
        return (from.name || '').split(' ')[0] || email.split('@')[0] || ''
      }
    },

    HmrSubject: {
      extends: 'HomePosterSoftInk',
      tag: 'span',
      fontSize: 'Z1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      // An empty subject is a real mail, not an error — mail's own list shows
      // the same placeholder rather than a blank line.
      text: (el, s) =>
        (s && s.subject) || el.call('polyglot', 'workspace.widget.mailInbox.noSubject')
    }
  },

  // Gmail-scale: today reads as a clock, older reads as a date. `shortDate` is
  // the shell's own formatter, so the row needs no date code of its own.
  HmrTime: {
    extends: 'HomePosterSoftInk',
    tag: 'span',
    fontSize: 'Y2',
    whiteSpace: 'nowrap',
    flexShrink: '0',
    text: (el, s) => (s && s.lastMessageAt ? el.call('shortDate', s.lastMessageAt) : '')
  }
}
